import produce from 'immer';
import { create } from 'zustand';

import type { ImportStartedNotification } from 'state/signalr/signalrState';

import type { ActiveImportSeed, ImportsInProgressState } from './types';

export const IMPORTS_IN_PROGRESS_STORE = create<ImportsInProgressState>((set) => ({
  importsInProgress: {},
  loadedProjectIds: {},
  completedImportRunIds: {},

  setImportStarted: (event: ImportStartedNotification) =>
    set((state) =>
      produce(state, (draft) => {
        if (event.IsDryRun) {
          return;
        }

        delete draft.completedImportRunIds[event.ImportRunId];

        draft.importsInProgress[event.ImportRunId] = {
          importRunId: event.ImportRunId,
          projectId: event.ProjectId,
          proposalId: event.ProposalId,
          needsPolling: false
        };
      })
    ),

  setImportCompleted: (importRunId: string) =>
    set((state) =>
      produce(state, (draft) => {
        draft.completedImportRunIds[importRunId] = true;
        delete draft.importsInProgress[importRunId];
      })
    ),

  syncActiveImports: (projectId: string, activeImports: ActiveImportSeed[], seenImportRunIds: string[]) =>
    set((state) =>
      produce(state, (draft) => {
        const seen = new Set(seenImportRunIds);
        const activeIds = new Set(activeImports.map((item) => item.importRunId));

        // A run the server has seen but no longer reports active is confirmed terminal, so the stale-poll
        // window that the resurrection guard protects against is closed. Drop its terminal marker (both to
        // bound the set and to let a future re-run of the same run id be tracked again). A stale poll that
        // still lists the run as active leaves it in `activeIds`, so its marker is correctly kept.
        seenImportRunIds.forEach((importRunId) => {
          if (!activeIds.has(importRunId)) {
            delete draft.completedImportRunIds[importRunId];
          }
        });

        // Remove this project's imports that the server has seen but no longer reports active.
        // Still-active ids are excluded so the add-loop below can preserve their `needsPolling`.
        Object.keys(draft.importsInProgress).forEach((importRunId) => {
          const entry = draft.importsInProgress[importRunId];
          if (entry?.projectId === projectId && seen.has(importRunId) && !activeIds.has(importRunId)) {
            delete draft.importsInProgress[importRunId];
          }
        });

        activeImports.forEach((item) => {
          if (draft.completedImportRunIds[item.importRunId]) {
            return;
          }

          const existing = draft.importsInProgress[item.importRunId];
          draft.importsInProgress[item.importRunId] = {
            ...item,
            // Preserve the prior value for known imports (so a concurrent mount fetch can't flip a
            // live-caught import to polling); fresh discoveries were missed, so they need polling.
            needsPolling: existing ? existing.needsPolling : true
          };
        });

        draft.loadedProjectIds[projectId] = true;
      })
    ),

  markAllImportsNeedPolling: () =>
    set((state) =>
      produce(state, (draft) => {
        Object.values(draft.importsInProgress).forEach((entry) => {
          entry.needsPolling = true;
        });
      })
    ),

  markProjectLoaded: (projectId: string) =>
    set((state) =>
      produce(state, (draft) => {
        draft.loadedProjectIds[projectId] = true;
      })
    )
}));
